import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';
import styles from './OrganizationContentBlocksPage.module.css';
import Spinner from '../../atoms/Spinner/Spinner';
import OrganizationContentBlocksTemplate from '../../templates/OrganizationContentBlocksTemplate/OrganizationContentBlocksTemplate';
import { BlockItem } from '../../atoms/SortableImageBlockItem/SortableImageBlockItem';
import { EntityBlockMappingResponseDto } from '../../../store/buyerAPI/block/dto/response/entity-block-mapping-response.dto';
import { BlockType } from '../../../store/buyerAPI/block/dto/block-type';
import { ImageBlockResponseDto } from '../../../store/buyerAPI/block/dto/response/image-block-response.dto';
import { RichTextBlockResponseDto } from '../../../store/buyerAPI/block/dto/response/rich-text-block-response.dto';
import {
  deleteBlock,
  fetchBlocks,
  orderBlocks,
} from '../../../store/buyerAPI/block/reducers/block.slice';
import { EntityType } from '../../../store/buyerAPI/block/entity-type';

interface OrganizationContentBlocksPageProps {}

const OrganizationContentBlocksPage: FC<
  OrganizationContentBlocksPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { blocks, status } = useSelector(
    (state: RootState) => state.organizationBlockSlice
  );

  const [data, setData] = useState<BlockItem[]>();
  const [changeOrderEnabled, setChangeOrderEnabled] = useState(false);

  const processBlockData = useCallback(
    (resp: EntityBlockMappingResponseDto[]) => {
      const blocks = resp
        .map((block: EntityBlockMappingResponseDto) => {
          if (block && block.block) {
            let imageUrl: string | null = null;
            let backgroundImageUrl: string | null = null;
            let backgroundColor: string | null = null;
            let content: string | null = null;
            let title: string = '';
            let type = BlockType.ImageBlock;

            if (block.block.blockType === BlockType.ImageBlock) {
              const imageBlock = block.block as ImageBlockResponseDto;
              imageUrl = imageBlock.imageUrl;
              backgroundImageUrl = imageBlock.backgroundImageUrl;
              backgroundColor = imageBlock.backgroundColor;
              title = BlockType.ImageBlock;
            }

            if (block.block.blockType === BlockType.RichTextBlock) {
              const richTextBlock = block.block as RichTextBlockResponseDto;
              content = richTextBlock.content;
              title = BlockType.RichTextBlock;
              type = BlockType.RichTextBlock;
            }

            return {
              blockId: block.block.id,
              id: block.id,
              sortOrder: block.sortOrder,
              imageUrl,
              backgroundImageUrl,
              backgroundColor,
              content,
              title,
              type,
            } as BlockItem;
          }
          return null;
        })
        .filter(Boolean);

      setData(blocks as BlockItem[]);
    },
    []
  );

  useEffect(() => {
    dispatch(fetchBlocks(organizationId!)).unwrap().then(processBlockData);
  }, [dispatch, organizationId, processBlockData]);

  const onReorder = (items: BlockItem[], from: number, to: number) => {
    setData(items);
    if (!blocks) return;
    let firstInd = blocks.findIndex((item) => item.sortOrder === from);
    let secondInd = blocks.findIndex((item) => item.sortOrder === to);
    if (firstInd === -1 || secondInd === -1) return;
    const blocksToOrder = items.map((item: BlockItem) => ({
      entityId: organizationId ?? '',
      entityType: EntityType.Buyer,
      sortOrder: item.sortOrder,
      blockId: item.blockId,
      id: item.id,
      published: true,
    }));
    dispatch(orderBlocks(blocksToOrder));
  };

  const handleEdit = async (blockId: string, type: BlockType) => {
    const blockType = type === BlockType.ImageBlock ? 'image' : 'text';
    const path = `/organizations/edit/${organizationId}/${blockType}-block/edit/${blockId}`;
    navigate(path);
  };

  const handleDelete = async (blockId: string) => {
    await dispatch(
      deleteBlock({ existingId: blockId, organizationId: organizationId! })
    );
    dispatch(fetchBlocks(organizationId!)).unwrap().then(processBlockData);
  };

  if (status === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      <OrganizationContentBlocksTemplate
        blockItems={data ?? []}
        setBlockItems={onReorder}
        changeOrderEnabled={changeOrderEnabled}
        setChangeOrderEnabled={setChangeOrderEnabled}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
    </div>
  );
};

export default OrganizationContentBlocksPage;
